import { createSlice } from "@reduxjs/toolkit"
import { DropdownObj } from "../../pages/Common/commonTypes"
import { DEAL_TEMPLATES, DEAL_TEMPLATES_OPTIONS, SCOPE_OF_WORK_OPTIONS } from "./config/projectFormConstants"
import { getTemplateFromSow, SOW_MAPPING } from "./config/mapping"
import { EFormModule } from "."
import { EAccountRole } from "../../enums/commonEnums"
import { SiteChargerObj } from "../../pages/Sites/SiteService"

export interface IDealSowForm {
    deal_type: DropdownObj
    deal_scope_of_work: DropdownObj[]
    deal_template: DropdownObj
}

export interface IMaterialDispatchForm {
    project_name: string
    visit_by_date: string
    visit_by_time: DropdownObj
    source_site_id: DropdownObj
    source_address: string
    source_pincode: string
    source_state: DropdownObj
    source_city: DropdownObj
    source_poc: {
        name: string
        phone: string
        email: string
    }
    destination_poc: {
        name: string
        phone: string
        email: string
    }
    file?: string
    materials?: IMaterialDetail[]
    source_latitude?: string | null
    source_longitude?: string | null
}

export interface IMaterialDetail {
    description: string
    quantity: number
    remarks?: string | null
    scope: string
    item_code: string
}
export interface ISiteForm {
    siteType: { id: number; title: string }
    siteName: string
    siteAddress: string
    pincode: string
    state: DropdownObj | null
    city: DropdownObj | null
    latitude: string
    longitude: string
    search_site_name: string
    search_pincode: string
    siteId: number | null
    selectedState: DropdownObj[]
    selectedCity: DropdownObj[]
    selectedCountry:DropdownObj
    addedBy: DropdownObj[]
    country:{id:number, title:string}
}

export interface ISoloSurveyForm {
    visit_by_date: string
    visit_by_time: DropdownObj
    spot_image: any[]
    load_available: string
    electricity_bill: any[]
    foundation_type: DropdownObj
    connection_type: DropdownObj
    survey_completion_date: string
    demand_load: string
    parking_slot: string
    sanctioned_load: string
    remarks: string
    project_name: string
    business_poc: DropdownObj
    chargers: {
        id: number
        type: DropdownObj | null
        quantity: string
        rating: string
    }[]
}

export interface IDemolitionChargerForm {
    is_entire_station_being_dismantled: boolean
    chargers: IDemolitionChargers[]
    selected_chargers: number[]
}

export interface IDemolitionChargers extends Omit<SiteChargerObj, 'oem' | 'sku_code'> {
    id?: string
    sku_code: DropdownObj | null
    oem: DropdownObj | null
    isSelected?: boolean
    isNew?: boolean
    charger_type_id?: string
}
interface POCFORM {
    name: string
    contact_number: string
    email: string
}
export interface IPaymentDetailsForm {
    total_amount: string
    specify_payment_terms: DropdownObj
    payment_terms: boolean
    payment_terms_obj: any[]
}

export interface ISiteFormDemolition {
    accountFormMap: {
        [key in EAccountRole]?: {
            accountId: DropdownObj | null
            billingAddress: DropdownObj | null
            billingContact: DropdownObj | null
        }
    }
    sourceSite: DropdownObj | null
    destinationSite: DropdownObj | null
    selectedAccountRoles: { id: EAccountRole; title: EAccountRole }[]
}

export interface IProjectDetailsForm {
    project_name: string
    deal_done_by: DropdownObj
    project_closure_date: string
    deal_docs: any
    purchase_order: any
    reason: DropdownObj
}
interface IRaiseTicketFormState {
    step: number
    project_id: string
    deal_sow_form: IDealSowForm
    form_module: EFormModule | ""
    material_dispatch_form: IMaterialDispatchForm
    solo_survey_form: ISoloSurveyForm
    sites_form: ISiteForm
    poc_details_form: POCFORM
    payment_details_form: IPaymentDetailsForm
    sites_form_demolition: ISiteFormDemolition
    project_details_form: IProjectDetailsForm
    demolition_charger_form: IDemolitionChargerForm
}

const initialState: IRaiseTicketFormState = {
    step: 1,
    project_id: "",
    deal_sow_form: {
        deal_type: {} as DropdownObj,
        deal_scope_of_work: [],
        deal_template: {} as DropdownObj,
    },
    poc_details_form: {
        name: "",
        email: "",
        contact_number: "",
    },
    solo_survey_form: {
        visit_by_date: "",
        visit_by_time: {} as DropdownObj,
        spot_image: [],
        load_available: "",
        electricity_bill: [],
        foundation_type: {} as DropdownObj,
        connection_type: {} as DropdownObj,
        business_poc: {} as DropdownObj,
        survey_completion_date: "",
        demand_load: "",
        sanctioned_load: "",
        parking_slot: "",
        remarks: "",
        project_name: "",
        chargers: [
            {
                id: Date.now(),
                type: null,
                quantity: "",
                rating: "",
            },
        ],
    },
    sites_form: {
        siteType: {} as DropdownObj,
        siteName: "",
        siteAddress: "",
        pincode: "",
        country:{id:101, title:"India"} as DropdownObj,
        state: null,
        city: null,
        latitude: "",
        longitude: "",
        search_site_name: "",
        search_pincode: "",
        siteId: null,
        selectedState: [],
        selectedCity: [],
        addedBy: [],
        selectedCountry:{id:101, title:"India"}
    },
    demolition_charger_form: {
        is_entire_station_being_dismantled: false,
        chargers: [
        ],
        selected_chargers: [],
    },
    sites_form_demolition: {
        accountFormMap: {
            [EAccountRole.BUYER]: {
                accountId: null,
                billingAddress: null,
                billingContact: null,
            },
        },
        sourceSite: null,
        destinationSite: null,
        selectedAccountRoles: [],
    },
    project_details_form: {
        project_name: "",
        deal_done_by: {} as DropdownObj,
        project_closure_date: "",
        deal_docs: null,
        purchase_order: null,
        reason: {} as DropdownObj,
    },
    material_dispatch_form: {
        project_name: "",
        visit_by_date: "",
        visit_by_time: {} as DropdownObj,
        source_site_id: {} as DropdownObj,
        source_address: "",
        source_pincode: "",
        source_state: {} as DropdownObj,
        source_city: {} as DropdownObj,
        source_poc: {
            name: "",
            phone: "",
            email: "",
        },
        destination_poc: {
            name: "",
            phone: "",
            email: "",
        },
        materials: [],
        file: "",
        source_latitude: "",
        source_longitude: "",
    },
    payment_details_form: {
        total_amount: "",
        specify_payment_terms: { id: 0, title: "" },
        payment_terms: false,
        payment_terms_obj: [],
    },
    form_module: "",
}

const RaiseTicketFormSlice = createSlice({
    name: "RaiseTicketForm",
    initialState: { ...initialState },
    reducers: {
        setStep: (state, { payload }: { payload: number }) => {
            state.step = payload
        },
        setProjectId: (state, { payload }: { payload: string }) => {
            state.project_id = payload
        },
        setDealSowForm: (
            state,
            { payload }: { payload: { value: DropdownObj | DropdownObj[]; key: keyof IDealSowForm } }
        ) => {
            if (payload.key === "deal_type") {
                if (!Array.isArray(payload.value)) {
                    state.deal_sow_form.deal_type = payload.value
                }
            } else if (payload.key === "deal_scope_of_work") {
                if (Array.isArray(payload.value)) {
                    const value = payload.value as DropdownObj[]
                    state.deal_sow_form.deal_scope_of_work = value
                    state.deal_sow_form.deal_template =
                        DEAL_TEMPLATES_OPTIONS.find(
                            option =>
                                option.id.toString() === getTemplateFromSow(value.map(item => item?.id))?.toString()
                        ) || ({} as DropdownObj)
                }
            }
        },
        setDealTemplate: (state, { payload }: { payload: DropdownObj }) => {
            state.deal_sow_form.deal_template = payload
            state.deal_sow_form.deal_scope_of_work = SOW_MAPPING[payload.id as DEAL_TEMPLATES].map(
                scope => SCOPE_OF_WORK_OPTIONS.find(option => option.id === scope) as DropdownObj
            )
        },
        setFormModule: (state, { payload }: { payload: EFormModule }) => {
            state.form_module = payload
        },
        setMaterialDispatchForm: (state, { payload }: { payload: IMaterialDispatchForm }) => {
            state.material_dispatch_form = payload
        },
        setSoloFormModule: (state, { payload }: { payload: ISoloSurveyForm }) => {
            state.solo_survey_form = payload
        },
        setPOCFormModule: (state, { payload }: { payload: POCFORM }) => {
            state.poc_details_form = payload
        },
        setSitesFormModule: (state, { payload }: { payload: ISiteForm }) => {
            state.sites_form = payload
        },
        setSitesFormDemolitionModule: (state, { payload }: { payload: ISiteFormDemolition }) => {
            state.sites_form_demolition = payload
        },
        setPaymentDetailsForm: (state, { payload }: { payload: IPaymentDetailsForm }) => {
            state.payment_details_form = payload
        },
        setProjectDetailsForm: (state, { payload }: { payload: IProjectDetailsForm }) => {
            state.project_details_form = payload
        },
        setDemolitionChargerForm: (state, { payload }: { payload: IDemolitionChargerForm }) => {
            state.demolition_charger_form = payload
        },
        setPaymentDetailsFormValue: (
            state,
            { payload }: { payload: { value: DropdownObj | string; key: keyof IPaymentDetailsForm } }
        ) => {
            ;(state.payment_details_form as any)[payload.key] = payload.value
        },
        resetRaiseTicketForm: () => initialState,
    },
})

export const {
    setStep,
    setProjectId,
    setDealSowForm,
    setDealTemplate,
    setSitesFormModule,
    setSoloFormModule,
    setFormModule,
    resetRaiseTicketForm,
    setPOCFormModule,
    setMaterialDispatchForm,
    setPaymentDetailsFormValue,
    setSitesFormDemolitionModule,
    setProjectDetailsForm,
    setDemolitionChargerForm,
} = RaiseTicketFormSlice.actions

export default RaiseTicketFormSlice.reducer
